import sanitizeHtml from 'sanitize-html';
import { DateTime } from 'luxon';

export const strictSanitizeOptions: sanitizeHtml.IOptions = {
  allowedTags: [],
  allowedAttributes: {},
  disallowedTagsMode: 'discard',
};

/**
 * Sanitization options that allow basic text formatting
 */
export const basicFormattingSanitizeOptions: sanitizeHtml.IOptions = {
  allowedTags: ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'ol', 'li'],
  allowedAttributes: {},
  disallowedTagsMode: 'discard',
};

/**
 * Sanitizes HTML string by removing all potentially dangerous content
 * @param html - The HTML string to sanitize
 * @param options - Custom sanitization options (defaults to strictSanitizeOptions)
 * @returns Sanitized HTML string
 */
export const sanitizeHtmlContent = (
  html: string | null | undefined,
  options: sanitizeHtml.IOptions = strictSanitizeOptions
): string => {
  if (html == null) {
    return '';
  }

  if (typeof html !== 'string') {
    html = String(html);
  }

  return sanitizeHtml(html, options);
};

/**
 * Recursively sanitizes all string values in an object
 * This is useful for sanitizing entire GraphQL input objects
 * 
 * @param obj - The object to sanitize
 * @param options - Custom sanitization options
 * @returns Sanitized object
 */
export const sanitizeObject = (
  obj: unknown,
  options: sanitizeHtml.IOptions = strictSanitizeOptions
): unknown => {
  if (obj == null) {
    return obj;
  }

  if (typeof obj === 'string') {
    return sanitizeHtmlContent(obj, options);
  }

  // Preserve Date objects - they don't have enumerable properties
  if (obj instanceof Date) {
    return obj;
  }

  // Convert Luxon DateTime objects to timestamps (numbers) for storage
  // This ensures DateTime objects are stored as numbers in MongoDB
  if (DateTime.isDateTime(obj)) {
    return obj.toMillis();
  }

  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeObject(item, options));
  }

  if (typeof obj === 'object') {
    const sanitized: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        sanitized[key] = sanitizeObject(obj[key], options);
      }
    }
    return sanitized;
  }

  return obj;
};

